//import { datadogLogs } from '@datadog/browser-logs'

function handleSpecialObjects(value: any): any {
  if (value instanceof Response) {
    return extractResponse(value)
  }
  if (value instanceof Error) {
    return extractError(value)
  }
  if (value instanceof Map) {
    return extractMap(value)
  }
  if (value instanceof Set) {
    return extractSet(value)
  }
  if (value instanceof Promise) {
    return 'Promise'
  }
  return recursiveExtract(value)
}

function extractResponse(response: Response): any {
  return {
    status: response.status,
    statusText: response.statusText,
    url: response.url,
    headers: [...response.headers.entries()],
    bodyUsed: response.bodyUsed,
    ok: response.ok,
    redirected: response.redirected,
    type: response.type,
  }
}

function extractError(error: Error): any {
  return {
    message: error.message,
    stack: error.stack,
    name: error.name,
  }
}

function extractMap(map: Map<any, any>): any {
  return {
    type: 'Map',
    entries: [...map.entries()],
  }
}

function extractSet(set: Set<any>): any {
  return {
    type: 'Set',
    values: [...set.values()],
  }
}

function serializeObject(error: any): {} {
  return recursiveExtract(error)
}

function doNotExtract(value: any): boolean {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'symbol' ||
    typeof value === 'bigint' ||
    typeof value === 'function' ||
    value === null ||
    value === undefined
  )
}

function recursiveExtract(error: any): any {
  if (error === null || error === undefined) {
    return error
  }

  try {
    if (Array.isArray(error)) {
      return error.map((item: any) =>
        doNotExtract(item) ? item : recursiveExtract(item)
      )
    }

    let extracted: any = {}
    const properties = Object.getOwnPropertyNames(error as object)

    for (const key of properties) {
      const value = error[key]

      if (doNotExtract(value)) {
        extracted[key] = value
      } else if (typeof value === 'object') {
        extracted[key] =
          Object.keys(value).length === 0 ? {} : handleSpecialObjects(value) //preserve empty objects
      }
    }
    return extracted
  } catch (e) {
    return error
  }
}

type LogLevel = 'info' | 'warn' | 'error'

/**
 * Logger class for logging messages to console or Datadog.
 */
export class Logger {
  private isLocal: boolean
  private datadogLogger: undefined

  /**
   * Creates an instance of Logger.
   * @param isLocal - Indicates if the logger should log to console (true) or Datadog (false).
   * @param datadogLogger - The datadogLogs instance to use for logging.
   */
  constructor(isLocal: boolean, datadogLogger?: undefined) {
    this.isLocal = isLocal
    this.datadogLogger = datadogLogger
  }

  /**
   * Logs a message with a specified log level and optional object.
   * @param message - The message to log.
   * @param level - The log level ('info', 'warn', 'error').
   * @param object - Optional object to log.
   */
  log(message: string, level: LogLevel = 'info', object?: any) {
    if (this.isLocal) {
      this.consoleLog(message, level, object)
    } else {
      this.datadogLog(message, level, object)
    }
  }

  /**
   * Logs an info message.
   * @param message - The message to log.
   * @param object - Optional object to log.
   */
  info(message: string, object?: any) {
    this.log(message, 'info', object)
  }

  /**
   * Logs a warning message.
   * @param message - The message to log.
   * @param object - Optional object to log.
   */
  warn(message: string, object?: any) {
    this.log(message, 'warn', object)
  }

  /**
   * Logs an error message.
   * @param message - The message to log.
   * @param object - Optional object to log.
   */
  error(message: string, object?: any) {
    this.log(message, 'error', object)
  }

  /**
   * Sets the context for the Datadog logger.
   * @param context - The context to set.
   */
  setContext(context: Record<string, any>) {
    if (this.datadogLogger) {
      //this.datadogLogger.setContext(context)
    } else {
      console.warn('Datadog logger instance is not defined')
    }
  }

  private consoleLog(message: string, level: LogLevel, object?: any) {
    if (object) {
      // eslint-disable-next-line no-console
      console[level](message, { logData: serializeObject(object) })
    } else {
      // eslint-disable-next-line no-console
      console[level](message)
    }
  }

  private datadogLog(message: string, level: LogLevel, object?: any) {
    if (this.datadogLogger) {
      const logData = object ? { logData: serializeObject(object) } : {}
      //this.datadogLogger[level](message, logData)
    } else {
      console.warn('Datadog logs instance is not defined')
    }
  }
}
